import { Alert, Button, Card, Modal, useInitialFocus } from '@playerbase/ui';

type DisconnectConfirmDialogProps = {
  guildName: string;
  open: boolean;
  busy: boolean;
  errorMessage: string | null;
  onConfirm: () => void;
  onCancel: () => void;
};

// Confirms the destructive disconnect action. The copy is explicit about
// what stops, what stays, and that the bot will leave the server - so the
// user can act without surprise. Cancel is auto-focused so Enter doesn't
// trigger the destructive primary by mistake.
//
// Composes via the Card primitive (Header / Content / Footer) so spacing,
// dividers, and tonal surfaces stay in lockstep with the studio invite
// modal and the rest of the portal's modals.
export function DisconnectConfirmDialog({
  guildName,
  open,
  busy,
  errorMessage,
  onConfirm,
  onCancel,
}: DisconnectConfirmDialogProps): JSX.Element {
  const cancelRef = useInitialFocus<HTMLButtonElement>();

  return (
    <Modal
      open={open}
      onClose={onCancel}
      busy={busy}
      labelledById="discord-disconnect-title"
      initialFocusRef={cancelRef}
    >
      <Card>
        <Card.Header>
          <Card.Title id="discord-disconnect-title">
            Disconnect {guildName}?
          </Card.Title>
          <Card.Description>
            The bot will leave {guildName} on Discord and stop ingesting messages
            from this server.
          </Card.Description>
        </Card.Header>
        <Card.Content>
          <div className="text-sm text-text-muted leading-relaxed space-y-2">
            <p>
              Channel monitoring stops, and the bot will leave {guildName}. New
              messages will not flow into the Feedback Pool until you reconnect.
            </p>
            <p>
              Feedback already captured stays - existing records remain
              searchable and addressable in the portal.
            </p>
          </div>
          {errorMessage !== null && (
            <Alert tone="danger" className="mt-4">
              {errorMessage}
            </Alert>
          )}
        </Card.Content>
        <Card.Footer className="justify-end">
          <Button
            ref={cancelRef}
            variant="ghost"
            size="md"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button variant="danger" size="md" onClick={onConfirm} disabled={busy}>
            {busy ? 'Disconnecting…' : 'Disconnect server'}
          </Button>
        </Card.Footer>
      </Card>
    </Modal>
  );
}
